import React, { FC } from 'react'
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '@store/store'
import {
  selectIsPlaylistIdInProgress,
  selectPlaylistById,
} from '@store/slices/playlist.slice'
import { isPathRegex } from '@shared/tools/path.tools'
import AppButton from '@components/shared/appButton'
import { AISlotModel, selectAiSlotsByIds } from '@store/slices/aiSlot.slice'
import { NarrationModel } from '@store/slices/narration.slice'
import {
  handleGeneratePlaylistOutline,
  handleNarratePlaylist,
  handlePublishUpdatedForPlaylist,
  handleTranscribePlaylist,
  handleUpdatePlaylistFromRadio,
} from '@services/playlist.service'
import { selectPlaylistNarrations } from '@store/selectors/playlist.selectors'

const ActionButtons: FC<{ selectedDate?: Date }> = () => {
  const { playlistId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const playlistLoading = useSelector((state: RootState) =>
    playlistId ? selectIsPlaylistIdInProgress(state, playlistId) : null
  )
  const aiSlots: AISlotModel[] = useSelector((state: RootState) =>
    playlist?.id ? selectAiSlotsByIds(state, playlist.aiSlots) : null
  )
  const playlistNarrations: NarrationModel[] = useSelector((state: RootState) =>
    selectPlaylistNarrations(state, aiSlots)
  )

  const onGenerateOutlineClick = () => {
    if (playlist) return handleGeneratePlaylistOutline(playlist)
  }

  const onNarratePlaylistClick = () => {
    if (playlist) return handleNarratePlaylist(playlist)
  }

  const onTranscribePlaylistClick = () => {
    if (playlist) return handleTranscribePlaylist(playlist)
  }

  const onPublishPlaylistsUpdatedClick = () => {
    if (playlist) return handlePublishUpdatedForPlaylist(playlist)
  }

  const isGeneratePlaylistNarrationsDisabled = (): boolean => {
    return (
      !aiSlots ||
      !aiSlots.some((aiSlot) => aiSlot.aiEntryMetadata) ||
      aiSlots.length === 0
    )
  }

  const isPublishPlaylistsUpdatedDisabled = (): boolean => {
    return !(
      playlistNarrations &&
      playlistNarrations.length > 0 &&
      playlistNarrations.some(
        (narration) =>
          narration.audioCreated &&
          narration.uploaded &&
          new Date(narration.uploaded) < new Date(narration.audioCreated)
      )
    )
  }

  const onUpdatePlaylistFromRadio = () => {
    if (playlist) return handleUpdatePlaylistFromRadio(playlist)
  }

  const DramaturgButtons: FC = () => (
    <div className={'d-flex gap-2'}>
      <Button
        variant={'secondary'}
        onClick={() => navigate(`/playlist/${playlistId}`)}
      >
        Moderátor
      </Button>
      <AppButton
        isLoading={playlistLoading}
        buttonText={'Přegenerovat vše'}
        onClick={onGenerateOutlineClick}
        buttonVariant={'primary'}
        disabled={aiSlots && aiSlots.length === 0}
      ></AppButton>
    </div>
  )
  const ModeratorButtons: FC = () => (
    <div className={'d-flex gap-2'}>
      <Button
        variant={'secondary'}
        onClick={() => navigate(`/playlist/${playlistId}/outline`)}
      >
        Dramaturg
      </Button>
      <Button
        variant={'secondary'}
        onClick={() => navigate(`/playlist/${playlistId}/audio`)}
      >
        Revize audia
      </Button>
      <AppButton
        isLoading={playlistLoading}
        buttonVariant={'primary'}
        buttonText={'Přegenerovat vše'}
        disabled={isGeneratePlaylistNarrationsDisabled()}
        onClick={onNarratePlaylistClick}
      />
      <AppButton
        isLoading={playlistLoading}
        buttonVariant={'primary'}
        buttonText={'Přegenerovat dialogy'}
        disabled={isGeneratePlaylistNarrationsDisabled()}
        onClick={onTranscribePlaylistClick}
      />
      <AppButton
        isLoading={playlistLoading}
        buttonVariant={'warning'}
        buttonText={'Aktualizovat playlist'}
        onClick={onUpdatePlaylistFromRadio}
      />
    </div>
  )
  const AudioButtons: FC = () => (
    <div className={'d-flex gap-2'}>
      <Button
        variant={'secondary'}
        onClick={() => navigate(`/playlist/${playlistId}/`)}
      >
        Moderátor
      </Button>
      <Button
        variant={'secondary'}
        onClick={() => navigate(`/playlist/${playlistId}/published`)}
      >
        Přehled Publikace
      </Button>
      <AppButton
        isLoading={playlistLoading}
        buttonVariant={'success'}
        buttonText={'Publikovat aktualizované'}
        disabled={isPublishPlaylistsUpdatedDisabled()}
        onClick={onPublishPlaylistsUpdatedClick}
      />
    </div>
  )

  const PublishedButtons: FC = () => (
    <div className={'d-flex gap-2'}>
      <Button
        variant="secondary"
        onClick={() => navigate(`/playlist/${playlistId}/audio`)}
      >
        Revize audia
      </Button>
    </div>
  )

  return (
    <>
      {isPathRegex(
        location.pathname,
        new RegExp(`^/playlist/${playlistId}/outline.*$`)
      ) && <DramaturgButtons></DramaturgButtons>}
      {isPathRegex(
        location.pathname,
        new RegExp(`^/playlist/${playlistId}(?!/outline|/audio|/published).*$`)
      ) && <ModeratorButtons></ModeratorButtons>}
      {!!matchPath(location.pathname, `/playlist/${playlistId}/audio`) && (
        <AudioButtons></AudioButtons>
      )}
      {!!matchPath(location.pathname, `/playlist/${playlistId}/published`) && (
        <PublishedButtons></PublishedButtons>
      )}
    </>
  )
}

export default ActionButtons
